import { cn, ToastProps } from '@heroui/react';

/**
 * Wspólne, kompaktowe style toastów (panel-app).
 *
 * HeroUI domyślnie renderuje toast jako kartę 356px z paddingiem 12px i chowa
 * przycisk zamykania (`opacity-0 group-hover:opacity-100`). Tutaj zmniejszamy
 * wysokość/typografię oraz na stałe pokazujemy „X", żeby toast dało się zamknąć
 * bez czekania na timeout (i bez zgadywania, że trzeba najechać myszką).
 */

export type ToastTone = 'danger' | 'success' | 'warning' | 'primary' | 'default';

/** Krótkie czasy życia toastów — komunikat ma nie blokować widoku. */
export const TOAST_TIMEOUT = {
  /** Potwierdzenia („zapisano", „skopiowano"). */
  short: 1800,
  /** Ostrzeżenia i błędy — trochę więcej czasu na przeczytanie. */
  default: 2500,
  /** Komunikaty wymagające reakcji (wygasła sesja, limit AI). */
  long: 4000,
} as const;

type ToastClassNames = NonNullable<ToastProps['classNames']>;

const resetStyles = '!border-none !shadow-none !outline-none';

const toastBase = cn([
  '!bg-white',
  'dark:!bg-zinc-900',
  '!bg-none',
  '!backdrop-blur-none',
  '!border-none',
  '!shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]',
  'dark:!shadow-[0_4px_16px_rgba(0,0,0,0.32),0_1px_4px_rgba(0,0,0,0.16)]',
  '!outline-none',
  '!p-0',
  '!my-0.5',
  '!min-h-0',
  '!rounded-lg',
  '!relative',
  '!w-full',
  'sm:!w-[320px]',
]);

// Wiersz ikona + tekst — domyślny gap-x-4 jest za duży dla kompaktowej wersji.
const toastContent = cn([
  resetStyles,
  '!p-0',
  '!m-0',
  '!gap-x-2',
  '[&_div]:!border-none',
  '[&_div]:!shadow-none',
  '[&_div]:!outline-none',
]);

// Kolumna z tytułem i opisem — to ona nadaje toastowi padding (base ma !p-0).
// UWAGA: bez własnego z-index i bez tła — inaczej biała kolumna zamalowuje
// przycisk zamykania (jest wyżej w DOM, ale niżej w kontekście stosu).
const toastWrapper = cn([
  '!rounded-lg',
  '!bg-transparent',
  '!py-2',
  '!pl-0',
  '!pr-7',
  '!gap-y-0',
  '!min-h-0',
  '!border-none',
  '!outline-none',
  '!relative',
]);

const toastTitle = cn(['font-normal', '!text-[13px]', '!leading-[1.25]', '!me-0', resetStyles]);

const toastDescription = cn([
  '!text-[11px]',
  '!leading-[1.35]',
  '!me-0',
  '!mt-0.5',
  '!text-default-500',
  resetStyles,
]);

// „X" na stałe widoczny (HeroUI domyślnie odsłania go dopiero na hover).
const toastCloseButton = cn([
  resetStyles,
  '!absolute',
  '!z-10',
  '!top-1',
  '!right-1',
  '!w-5',
  '!h-5',
  '!min-w-5',
  '!min-h-5',
  '!p-0',
  '!opacity-100',
  '!pointer-events-auto',
  '!bg-transparent',
  'hover:!bg-default-100',
  'dark:hover:!bg-zinc-800',
  '!rounded-md',
  '!text-default-400',
  'hover:!text-default-600',
  'dark:!text-default-500',
  'dark:hover:!text-default-300',
]);

const toastCloseIcon = cn([
  resetStyles,
  '!w-3',
  '!h-3',
  '!p-0',
  '!bg-transparent',
  '!rounded-none',
]);

// Ikona to samo `<svg>` (HeroUI klonuje element z klasami slotu), więc padding
// zmniejsza rysowany glif wewnątrz kolorowego kółka.
const iconBase = [
  '!w-5',
  '!h-5',
  '!min-w-5',
  '!min-h-5',
  '!ml-2.5',
  '!p-1',
  '!rounded-full',
  '!shrink-0',
  '!fill-none',
  resetStyles,
];

const toneIcon: Record<ToastTone, string> = {
  danger: cn([...iconBase, '!bg-danger-50', 'dark:!bg-danger-900/20', '!text-danger-500']),
  success: cn([...iconBase, '!bg-success-50', 'dark:!bg-success-900/20', '!text-success-500']),
  warning: cn([...iconBase, '!bg-warning-50', 'dark:!bg-warning-900/20', '!text-warning-500']),
  primary: cn([...iconBase, '!bg-primary-50', 'dark:!bg-primary-900/20', '!text-primary-500']),
  default: cn([...iconBase, '!bg-default-100', 'dark:!bg-zinc-800', '!text-default-500']),
};

const toneTitle: Record<ToastTone, string> = {
  danger: '!text-danger-600 dark:!text-danger-400',
  success: '!text-success-600 dark:!text-success-400',
  warning: '!text-warning-600 dark:!text-warning-400',
  primary: '!text-primary-600 dark:!text-primary-400',
  default: '!text-foreground',
};

export const getToastClassNames = (tone: ToastTone = 'default'): ToastClassNames => ({
  base: toastBase,
  wrapper: toastWrapper,
  content: toastContent,
  title: cn([toastTitle, toneTitle[tone]]),
  description: toastDescription,
  icon: toneIcon[tone],
  closeButton: toastCloseButton,
  closeIcon: toastCloseIcon,
});

/**
 * Wspólne propsy `addToast` — kompaktowy wygląd + zawsze widoczny „X".
 * `color` zostaje `default` (tło toasta jest białe), a `severity` decyduje tylko
 * o domyślnej ikonie, gdy wywołanie nie poda własnej.
 */
export const compactToastProps = (tone: ToastTone = 'default'): Partial<ToastProps> => ({
  color: 'default',
  severity: tone,
  variant: 'bordered',
  radius: 'md',
  hideCloseButton: false,
  classNames: getToastClassNames(tone),
});
